import { Component } from '@theme/component';
import { STORAGE_KEY, cleanItems, validQuantity, addItem, quoteText, csvText } from '@theme/medorra-quote-core';

let memory;
let persistent = true;
function readItems() {
  if (memory) return memory;
  try { memory = cleanItems(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')); }
  catch { memory = []; persistent = false; }
  return memory;
}
function writeItems(items) {
  memory = cleanItems(items);
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(memory)); persistent = true; }
  catch { persistent = false; }
  window.dispatchEvent(new Event('medorra:quote-change'));
}

/** @extends {Component<{option:HTMLSelectElement,quantity:HTMLInputElement,add:HTMLButtonElement,status:HTMLElement,price:HTMLElement}>} */
class MedorraQuoteAdd extends Component {
  requiredRefs = ['option', 'quantity', 'add', 'status'];
  connectedCallback() { super.connectedCallback(); this.refs.add.disabled = false; this.updatePrice(); }
  updatedCallback() { super.updatedCallback(); this.refs.add.disabled = false; this.updatePrice(); }
  updatePrice() {
    const selected = this.refs.option.selectedOptions[0];
    if (this.refs.price && selected) this.refs.price.textContent = selected.dataset.priceLabel || '';
  }
  add() {
    const { option, quantity, status } = this.refs;
    if (!quantity.reportValidity()) return;
    const count = validQuantity(quantity.value);
    if (count === null) return;
    const selected = option.selectedOptions[0];
    if (!selected) return;
    try {
      writeItems(addItem(readItems(), {
        id: `${this.dataset.productId}-${option.value}`, title: this.dataset.title || '',
        option: selected.textContent.trim(), pack: this.dataset.pack || '',
        url: this.dataset.url || '', quantity: count,
      }));
      status.textContent = persistent ? this.dataset.added : this.dataset.temporary;
    } catch { status.textContent = this.dataset.limit; }
  }
}

/** @extends {Component<{count:HTMLElement}>} */
class MedorraQuoteCount extends Component {
  #controller;
  connectedCallback() {
    super.connectedCallback(); this.#controller = new AbortController();
    window.addEventListener('medorra:quote-change', () => this.update(), { signal: this.#controller.signal });
    window.addEventListener('storage', event => { if (event.key === STORAGE_KEY) { memory = undefined; this.update(); } }, { signal: this.#controller.signal });
    this.update();
  }
  update() { if (this.refs.count) this.refs.count.textContent = String(readItems().length); }
  disconnectedCallback() { this.#controller?.abort(); super.disconnectedCallback(); }
}

/** @extends {Component<{items:HTMLElement,row:HTMLTemplateElement,empty:HTMLElement,actions:HTMLElement,status:HTMLElement}>} */
class MedorraQuoteList extends Component {
  requiredRefs = ['items', 'row', 'empty', 'actions', 'status'];
  #controller;
  connectedCallback() {
    super.connectedCallback(); this.#controller = new AbortController();
    window.addEventListener('medorra:quote-change', () => this.render(), { signal: this.#controller.signal });
    window.addEventListener('storage', event => { if (event.key === STORAGE_KEY) { memory = undefined; this.render(); } }, { signal: this.#controller.signal });
    this.render();
  }
  disconnectedCallback() { this.#controller?.abort(); super.disconnectedCallback(); }
  render() {
    const items = readItems(); const fragment = document.createDocumentFragment();
    for (const item of items) {
      const row = this.refs.row.content.cloneNode(true);
      row.querySelector('[data-row]').dataset.id = item.id;
      const link = row.querySelector('[data-title]'); link.textContent = item.title; link.href = item.url;
      row.querySelector('[data-option]').textContent = item.option;
      row.querySelector('[data-pack]').textContent = item.pack;
      const quantity = row.querySelector('input'); quantity.value = String(item.quantity);
      quantity.setAttribute('aria-label', `${this.dataset.quantityLabel}: ${item.title}, ${item.option}`);
      row.querySelector('button').setAttribute('aria-label', `${this.dataset.removeLabel}: ${item.title}, ${item.option}`);
      fragment.append(row);
    }
    this.refs.items.replaceChildren(fragment);
    this.refs.empty.hidden = items.length > 0; this.refs.actions.hidden = items.length === 0;
    this.refs.status.textContent = persistent ? '' : this.dataset.temporary;
  }
  /** @param {Event} event */
  changeQuantity(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.reportValidity()) return;
    const quantity = validQuantity(input.value); if (quantity === null) return;
    const id = input.closest('[data-row]')?.dataset.id;
    const position = readItems().findIndex(item => item.id === id);
    writeItems(readItems().map(item => item.id === id ? { ...item, quantity } : item));
    this.refs.items.querySelectorAll('input')[position]?.focus(); this.refs.status.textContent = this.dataset.updated;
  }
  /** @param {Event} event */
  remove(event) {
    const target = event.target; if (!(target instanceof Element)) return;
    const id = target.closest('[data-row]')?.dataset.id;
    const position = readItems().findIndex(item => item.id === id);
    writeItems(readItems().filter(item => item.id !== id));
    const buttons = this.refs.items.querySelectorAll('button');
    if (buttons.length) buttons[Math.min(position, buttons.length - 1)].focus();
    else this.refs.empty.querySelector('a')?.focus();
    this.refs.status.textContent = this.dataset.removed;
  }
  download() {
    const rows = [[this.dataset.productLabel, this.dataset.optionLabel, this.dataset.packLabel, this.dataset.quantityLabel], ...readItems().map(item => [item.title, item.option, item.pack, item.quantity])];
    const url = URL.createObjectURL(new Blob([csvText(rows)], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a'); link.href = url; link.download = 'Medorra-quote-list.csv'; link.click(); URL.revokeObjectURL(url);
  }
}

/** @extends {Component<{field:HTMLTextAreaElement,summary:HTMLElement,toggle:HTMLInputElement}>} */
class MedorraQuoteInquiry extends Component {
  #controller;
  connectedCallback() {
    super.connectedCallback(); this.#controller = new AbortController();
    window.addEventListener('medorra:quote-change', () => this.update(), { signal: this.#controller.signal });
    window.addEventListener('storage', event => { if (event.key === STORAGE_KEY) { memory = undefined; this.update(); } }, { signal: this.#controller.signal }); this.update();
  }
  update() {
    const items = readItems(); this.hidden = !items.length;
    if (this.refs.field) { this.refs.field.value = quoteText(items); this.refs.field.disabled = !items.length || !this.refs.toggle.checked; }
    if (this.refs.summary) this.refs.summary.textContent = (this.dataset.countLabel || '').replace('__count__', String(items.length));
  }
  disconnectedCallback() { this.#controller?.abort(); super.disconnectedCallback(); }
}
for (const [tag, type] of [['medorra-quote-add', MedorraQuoteAdd], ['medorra-quote-count', MedorraQuoteCount], ['medorra-quote-list', MedorraQuoteList], ['medorra-quote-inquiry', MedorraQuoteInquiry]]) {
  if (!customElements.get(tag)) customElements.define(tag, type);
}
